import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { useJudgeScoring } from '../components/curator/collections/v2/hooks/useJudging';

export default function JudgeScoring() {
  const { roundId } = useParams();
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [collectionName, setCollectionName] = useState(null);

  // Auth check
  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user || null);
      setAuthLoading(false);
    };
    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setUser(session?.user || null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Fetch collection name from the round
  const fetchCollectionName = async () => {
    if (!roundId) return;
    const { data } = await supabase
      .from('collection_judging_rounds')
      .select('collection_id, collections:collection_id(title)')
      .eq('id', roundId)
      .single();
    if (data?.collections?.title) {
      setCollectionName(data.collections.title);
    }
  };

  useEffect(() => {
    fetchCollectionName();
  }, [roundId]);

  // Re-fetch after auth in case anon query was blocked by RLS
  useEffect(() => {
    if (user && !collectionName) {
      fetchCollectionName();
    }
  }, [user]);

  if (authLoading) {
    return <PageShell collectionName={collectionName}><p style={{ color: '#94a3b8' }}>Loading…</p></PageShell>;
  }

  if (!user) {
    return <LoginPrompt roundId={roundId} collectionName={collectionName} />;
  }

  return <ScoringInterface roundId={roundId} user={user} collectionName={collectionName} />;
}

// ─── Login prompt for unauthenticated judges ───
function LoginPrompt({ roundId, collectionName }) {
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [usePassword, setUsePassword] = useState(false);
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const { error: err } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: false },
      });
      if (err) throw err;
      setOtpSent(true);
      setResendCountdown(60);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const { error: err } = await supabase.auth.verifyOtp({
        email,
        token: otpCode,
        type: 'email',
      });
      if (err) throw err;
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const { error: err } = await supabase.auth.signInWithPassword({ email, password });
      if (err) throw err;
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell collectionName={collectionName}>
      <div style={{ maxWidth: 400, margin: '4rem auto', textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>⚖️</div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.25rem' }}>Judging Panel</h1>
        <p style={{ color: '#64748b', marginBottom: '2rem' }}>Sign in to score artworks for this collection.</p>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '0.5rem 1rem', borderRadius: 8, marginBottom: '1rem', fontSize: '0.85rem' }}>
            {error}
          </div>
        )}

        {usePassword ? (
          <form onSubmit={handlePasswordLogin} style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={labelStyle}>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={inputStyle} />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required style={inputStyle} />
            </div>
            <button type="submit" disabled={loading} style={{ ...btnPrimary, width: '100%' }}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
            <button type="button" onClick={() => { setUsePassword(false); setError(null); }} style={{ ...btnLink, marginTop: '0.75rem' }}>
              ← Use email code instead
            </button>
          </form>
        ) : !otpSent ? (
          <form onSubmit={handleSendOtp} style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required style={inputStyle} placeholder="you@example.com" />
            </div>
            <button type="submit" disabled={loading} style={{ ...btnPrimary, width: '100%' }}>
              {loading ? 'Sending…' : 'Send Sign-In Code'}
            </button>
            <button type="button" onClick={() => { setUsePassword(true); setError(null); }} style={{ ...btnLink, marginTop: '0.75rem' }}>
              Use password instead
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} style={{ textAlign: 'left' }}>
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '1rem' }}>
              A 6-digit code was sent to <strong>{email}</strong>. Check your inbox.
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={labelStyle}>Verification Code</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                required
                style={{ ...inputStyle, textAlign: 'center', fontSize: '1.25rem', letterSpacing: '0.5em' }}
                placeholder="000000"
                autoFocus
              />
            </div>
            <button type="submit" disabled={loading || otpCode.length < 6} style={{ ...btnPrimary, width: '100%' }}>
              {loading ? 'Verifying…' : 'Verify & Sign In'}
            </button>
            <div style={{ marginTop: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button type="button" onClick={() => { setOtpSent(false); setOtpCode(''); setError(null); }} style={btnLink}>
                ← Change email
              </button>
              <button type="button" onClick={handleSendOtp} disabled={resendCountdown > 0 || loading} style={btnLink}>
                {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}
      </div>
    </PageShell>
  );
}

// ─── Main Scoring Interface ───
function ScoringInterface({ roundId, user, collectionName }) {
  const { data, loading, error, submitting, submitScore, refresh } = useJudgeScoring(roundId, user.id);
  const [expandedArtwork, setExpandedArtwork] = useState(null);
  const [noteInputs, setNoteInputs] = useState({});
  const [filterMode, setFilterMode] = useState('all');
  const [lightboxImage, setLightboxImage] = useState(null); // all | unscored | scored

  const artworks = useMemo(() => {
    if (!data?.artworks) return [];
    if (filterMode === 'unscored') return data.artworks.filter((a) => a.judge_score == null);
    if (filterMode === 'scored') return data.artworks.filter((a) => a.judge_score != null);
    return data.artworks;
  }, [data?.artworks, filterMode]);

  if (loading) return <PageShell collectionName={collectionName}><p style={{ color: '#94a3b8', textAlign: 'center', marginTop: '4rem' }}>Loading artworks…</p></PageShell>;

  if (error) {
    return (
      <PageShell collectionName={collectionName}>
        <div style={{ maxWidth: 480, margin: '4rem auto', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔒</div>
          <h2 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Access Denied</h2>
          <p style={{ color: '#64748b' }}>{error}</p>
        </div>
      </PageShell>
    );
  }

  if (!data) return null;

  const { round, progress } = data;
  const isWithinWindow = round?.is_within_window;
  const scoringMin = round?.scoring_min ?? 1;
  const scoringMax = round?.scoring_max ?? 5;
  const progressPct = progress?.total_artworks > 0 ? Math.round((progress.scored / progress.total_artworks) * 100) : 0;

  const handleScore = async (vcId, score) => {
    const notes = noteInputs[vcId] || null;
    await submitScore(vcId, score, notes);
  };

  return (
    <PageShell collectionName={collectionName}>
      {/* Header */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '1rem 1rem 0' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.25rem' }}>{round?.title || 'Judging Round'}</h1>
          {round?.description && <p style={{ color: '#64748b', fontSize: '0.9rem' }}>{round.description}</p>}
        </div>

        {/* Progress Bar */}
        <div style={{ background: '#f1f5f9', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              {progress?.scored} / {progress?.total_artworks} scored
            </span>
            <span style={{ fontSize: '0.85rem', color: '#6366f1', fontWeight: 600 }}>{progressPct}%</span>
          </div>
          <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3 }}>
            <div style={{ height: 6, background: '#6366f1', borderRadius: 3, width: `${progressPct}%`, transition: 'width 0.3s ease' }} />
          </div>
          {progress?.remaining > 0 && (
            <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '0.5rem' }}>
              {progress.remaining} remaining
            </div>
          )}
          {progress?.remaining === 0 && (
            <div style={{ fontSize: '0.85rem', color: '#16a34a', marginTop: '0.5rem', fontWeight: 600 }}>
              ✓ All artworks scored!
            </div>
          )}
        </div>

        {/* Status / Window Warning */}
        {!isWithinWindow && (
          <div style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e', padding: '0.75rem 1rem', borderRadius: 10, marginBottom: '1.25rem', fontSize: '0.85rem', textAlign: 'center' }}>
            ⏰ The judging window is not currently open. Scores cannot be submitted.
          </div>
        )}

        {/* Filter Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
          {[
            { key: 'all', label: `All (${data?.artworks?.length || 0})` },
            { key: 'unscored', label: `Unscored (${progress?.remaining || 0})` },
            { key: 'scored', label: `Scored (${progress?.scored || 0})` },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilterMode(f.key)}
              style={{
                background: filterMode === f.key ? '#6366f1' : '#f1f5f9',
                color: filterMode === f.key ? '#fff' : '#64748b',
                border: 'none',
                borderRadius: 8,
                padding: '6px 14px',
                fontSize: '0.8rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Artwork Grid */}
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 1rem 4rem' }}>
        {artworks.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#94a3b8', padding: '2rem' }}>No artworks to display.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' }}>
            {artworks.map((artwork) => (
              <ArtworkCard
                key={artwork.vc_id}
                artwork={artwork}
                scoringMin={scoringMin}
                scoringMax={scoringMax}
                isWithinWindow={isWithinWindow}
                submitting={submitting}
                expanded={expandedArtwork === artwork.vc_id}
                onToggleExpand={() => setExpandedArtwork(expandedArtwork === artwork.vc_id ? null : artwork.vc_id)}
                notes={noteInputs[artwork.vc_id] || ''}
                onNotesChange={(v) => setNoteInputs((prev) => ({ ...prev, [artwork.vc_id]: v }))}
                onScore={(score) => handleScore(artwork.vc_id, score)}
                onImageClick={() => setLightboxImage(artwork.cover_image)}
              />
            ))}
          </div>
        )}
      </div>
      {/* Fullscreen Lightbox */}
      {lightboxImage && (
        <div
          onClick={() => setLightboxImage(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.9)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'zoom-out',
          }}
        >
          <button
            onClick={() => setLightboxImage(null)}
            style={{
              position: 'absolute', top: 16, right: 16,
              background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
              fontSize: '1.5rem', width: 44, height: 44, borderRadius: '50%',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ×
          </button>
          <img
            src={lightboxImage}
            alt="Artwork"
            style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain', borderRadius: 4 }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </PageShell>
  );
}

// ─── Artwork Scoring Card ───
function ArtworkCard({ artwork, scoringMin, scoringMax, isWithinWindow, submitting, expanded, onToggleExpand, notes, onNotesChange, onScore, onImageClick }) {
  const scoreRange = [];
  for (let i = scoringMin; i <= scoringMax; i++) scoreRange.push(i);

  return (
    <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      {/* Image */}
      {artwork.cover_image && (
        <div style={{ position: 'relative', overflow: 'hidden', cursor: 'zoom-in' }} onClick={onImageClick}>
          <img
            src={artwork.cover_image}
            alt="Artwork"
            style={{ width: '100%', display: 'block', objectFit: 'cover', maxHeight: 260 }}
            loading="lazy"
          />
          {artwork.judge_score != null && (
            <div style={{ position: 'absolute', top: 8, right: 8, background: '#6366f1', color: '#fff', borderRadius: 8, padding: '4px 10px', fontSize: '0.85rem', fontWeight: 700 }}>
              {artwork.judge_score}
            </div>
          )}
        </div>
      )}

      {/* Artwork info */}
      <div style={{ padding: '0.75rem 1rem' }}>
        {artwork.title && (
          <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 2 }}>{artwork.title}</div>
        )}
        {(artwork.width || artwork.height || artwork.medium) && (
          <div style={{ fontSize: '0.75rem', color: '#64748b', lineHeight: 1.4 }}>
            {artwork.medium && <span>{artwork.medium}</span>}
            {artwork.medium && (artwork.width || artwork.height) && <span> · </span>}
            {(artwork.width || artwork.height) && (
              <span>{artwork.width}"{artwork.height ? ` × ${artwork.height}"` : ''}</span>
            )}
          </div>
        )}

        {/* Score Buttons */}
        {isWithinWindow && (
          <div style={{ marginTop: '0.75rem' }}>
            <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
              {scoreRange.map((score) => (
                <button
                  key={score}
                  onClick={() => onScore(score)}
                  disabled={submitting}
                  style={{
                    flex: 1,
                    padding: '8px 0',
                    border: artwork.judge_score === score ? '2px solid #6366f1' : '1px solid #d1d5db',
                    borderRadius: 8,
                    background: artwork.judge_score === score ? '#eef2ff' : '#fff',
                    color: artwork.judge_score === score ? '#4338ca' : '#374151',
                    fontWeight: artwork.judge_score === score ? 700 : 500,
                    fontSize: '0.9rem',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {score}
                </button>
              ))}
            </div>

            {/* Notes toggle */}
            <button
              onClick={onToggleExpand}
              style={{ background: 'none', border: 'none', color: '#6366f1', fontSize: '0.75rem', cursor: 'pointer', padding: 0 }}
            >
              {expanded ? '▲ Hide notes' : '▼ Add notes'}
            </button>

            {expanded && (
              <textarea
                value={notes}
                onChange={(e) => onNotesChange(e.target.value)}
                placeholder="Optional notes about this piece…"
                rows={2}
                style={{ ...inputStyle, marginTop: 6, fontSize: '0.8rem', resize: 'vertical' }}
              />
            )}
          </div>
        )}

        {/* Already scored indicator when window closed */}
        {!isWithinWindow && artwork.judge_score != null && (
          <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#6366f1', fontWeight: 600 }}>
            Scored: {artwork.judge_score}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Layout Shell ───
function PageShell({ children, collectionName }) {
  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <header style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '0.75rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{collectionName || 'VibrantCast'}</div>
        <span style={{ fontSize: '0.8rem', color: '#6366f1', fontWeight: 500 }}>Judging Panel</span>
      </header>
      {children}
    </div>
  );
}

// ─── Style constants ───
const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#334155', marginBottom: 4 };

const inputStyle = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  fontSize: '0.85rem',
  outline: 'none',
  boxSizing: 'border-box',
};

const btnPrimary = {
  background: '#6366f1',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '0.6rem 1.25rem',
  fontSize: '0.9rem',
  fontWeight: 500,
  cursor: 'pointer',
};
const btnLink = {
  background: 'none',
  border: 'none',
  color: '#6366f1',
  fontSize: '0.8rem',
  cursor: 'pointer',
  padding: 0,
};
